import React, { useEffect, useState } from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import sh from "bundles/common/utils/sh";

const POLL_INTERVAL_MS = 60000;
const MAX_VISIBLE = 13;

// Label + colors per timeline event type, mirroring the palette used in
// CandidateTimelinePanel / CompanyTimelinePanel so the feed matches the timelines.
const EVENT_CONFIG = {
  call_outcome: { label: "logged a call", color: "hsl(262 50% 35%)", bg: "hsl(262 40% 95%)" },
  email: { label: "email sent", color: "hsl(217 70% 30%)", bg: "hsl(217 50% 95%)" },
  note: { label: "note added", color: "hsl(150 38% 17%)", bg: "hsl(150 30% 95%)" },
  recruiter_note: { label: "recruiter note added", color: "hsl(200 45% 30%)", bg: "hsl(200 35% 95%)" },
  candidate_note_written: { label: "candidate note added", color: "hsl(45 60% 35%)", bg: "hsl(45 45% 95%)" },
  signup: { label: "signed up", color: "hsl(200 60% 35%)", bg: "hsl(200 45% 95%)" },
  kanban_move: { label: "moved in pipeline", color: "hsl(38 70% 30%)", bg: "hsl(38 50% 95%)" },
  kanban_column_created: { label: "pipeline column created", color: "hsl(270 40% 38%)", bg: "hsl(270 30% 95%)" },
  pipeline_added: { label: "added to a pipeline", color: "hsl(150 50% 30%)", bg: "hsl(150 40% 95%)" },
  pipeline_removed: { label: "removed from a pipeline", color: "hsl(0 40% 45%)", bg: "hsl(0 30% 95%)" },
  job_application_created: { label: "application created", color: "hsl(180 35% 28%)", bg: "hsl(180 30% 95%)" },
  job_application_received: { label: "application received", color: "hsl(180 35% 28%)", bg: "hsl(180 30% 95%)" },
  job_application_closed: { label: "application closed", color: "hsl(0 55% 38%)", bg: "hsl(0 40% 95%)" },
  job_offer_published: { label: "job offer published", color: "hsl(142 50% 30%)", bg: "hsl(142 40% 95%)" },
  job_offer_closed: { label: "job offer closed", color: "hsl(0 55% 38%)", bg: "hsl(0 40% 95%)" },
  recruiter_interest: { label: "recruiter showed interest", color: "hsl(330 50% 40%)", bg: "hsl(330 40% 95%)" },
  candidate_interest: { label: "showed interest in a candidate", color: "hsl(330 50% 40%)", bg: "hsl(330 40% 95%)" },
  suggested_to_company: { label: "suggested to a company", color: "hsl(38 60% 40%)", bg: "hsl(38 45% 95%)" },
  suggestion_received: { label: "received a suggestion", color: "hsl(38 60% 40%)", bg: "hsl(38 45% 95%)" },
  credit_purchased: { label: "credit purchased", color: "hsl(262 50% 35%)", bg: "hsl(262 40% 95%)" },
  credit_expired: { label: "credit expired", color: "hsl(30 60% 35%)", bg: "hsl(30 45% 95%)" },
  recruiter_joined: { label: "recruiter joined", color: "hsl(200 60% 35%)", bg: "hsl(200 45% 95%)" },
  milestone_added: { label: "milestone reached", color: "hsl(280 45% 38%)", bg: "hsl(280 35% 95%)" },
  testimonial_added: { label: "testimonial added", color: "hsl(170 45% 30%)", bg: "hsl(170 35% 95%)" },
};

const DEFAULT_EVENT_CONFIG = { color: "hsl(150 10% 46%)", bg: "hsl(140 10% 95%)" };

// Free-text "other" reasons are stored in the same array as the known keys,
// so anything unmapped falls through as-is.
const DENIAL_REASON_LABELS = {
  wrong_diploma: "Wrong diploma",
  wrong_xp: "Wrong experience",
  insufficient_languages: "Insufficient language skills",
  closed_position: "Job not open anymore",
  other: "Other",
};

function denialReasonLabel(reason) {
  return DENIAL_REASON_LABELS[reason] || String(reason).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function eventConfig(type) {
  if (EVENT_CONFIG[type]) return EVENT_CONFIG[type];
  return { ...DEFAULT_EVENT_CONFIG, label: type ? type.replace(/_/g, " ") : "activity" };
}

function relativeTime(dateStr) {
  if (!dateStr) return null;
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.floor((now - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function truncate(text, max = 90) {
  if (!text) return "";
  const clean = String(text).replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
}

function joinParts(parts) {
  return parts.filter(Boolean).join(" · ");
}

// Secondary, event-specific detail line built from the timeline event payload/actor.
function eventDetail(act) {
  const p = act.payload || {};
  const actor = act.actor?.name;

  switch (act.type) {
  case "email":
    return joinParts([
      p.recipient_email || p.recruiter_email || p.recruiter_name,
      p.email_title,
    ]);
  case "call_outcome":
    return joinParts([actor && `by ${actor}`, p.external_profile_name, p.outcome && p.outcome.replace(/_/g, " ")]);
  case "note":
    return joinParts([actor && `by ${actor}`, truncate(p.content)]);
  case "recruiter_note":
    return joinParts([p.recruiter_name, truncate(p.content)]);
  case "candidate_note_written":
    return joinParts([p.candidate_name, p.recruiter_name && `by ${p.recruiter_name}`, truncate(p.content)]);
  case "job_application_created":
    return joinParts([p.job_title, p.company_name, p.sent_from_talent_team && "talent team"]);
  case "job_application_received":
    return joinParts([p.candidate_name, p.job_title, p.sent_from_talent_team && "talent team"]);
  case "job_application_closed":
    return joinParts([p.candidate_name, p.job_title, Array.isArray(p.denial_reasons) && p.denial_reasons.length ? p.denial_reasons.map(denialReasonLabel).join(", ") : null]);
  case "job_offer_published":
  case "job_offer_closed":
    return p.job_title || "";
  case "recruiter_interest":
    return joinParts([actor, p.company_name]);
  case "candidate_interest":
    return joinParts([p.recruiter_name, p.candidate_name && `→ ${p.candidate_name}`]);
  case "suggested_to_company":
    return joinParts([p.company_name, p.status, p.selected_by]);
  case "suggestion_received":
    return joinParts([p.candidate_name, p.status, p.selected_by]);
  case "credit_purchased":
    return joinParts([p.credit_type, p.status]);
  case "credit_expired":
    return p.credit_type || "";
  case "kanban_move":
    return joinParts([p.candidate_name, (p.from_column || p.to_column) && `${p.from_column || "?"} → ${p.to_column || "?"}`]);
  case "kanban_column_created":
    return p.column_name || "";
  case "pipeline_added":
  case "pipeline_removed":
    return joinParts([p.pipeline_title, actor && `by ${actor}`]);
  case "signup":
    return joinParts([p.external_profile_name, p.source]);
  case "recruiter_joined":
    return joinParts([p.recruiter_name, p.email]);
  case "milestone_added":
    return joinParts([p.title, p.year]);
  case "testimonial_added":
    return joinParts([p.author_name, p.role]);
  default:
    return "";
  }
}

// Small status pills for email deliverability/engagement.
function emailStatusTags(act) {
  if (act.type !== "email") return [];
  const p = act.payload || {};
  const tags = [];
  if (p.hard_bounced) tags.push({ label: "bounced", color: "hsl(0, 72%, 51%)" });
  if (p.unsubscribed) tags.push({ label: "unsubscribed", color: "hsl(0, 72%, 51%)" });
  if (p.click_count > 0) tags.push({ label: `clicked${p.click_count > 1 ? ` ×${p.click_count}` : ""}`, color: "hsl(262, 65%, 55%)" });
  if (p.open_count > 0) tags.push({ label: `opened${p.open_count > 1 ? ` ×${p.open_count}` : ""}`, color: "hsl(142, 71%, 45%)" });
  if (!tags.length) {
    if (p.delivered) tags.push({ label: "delivered", color: "hsl(150, 10%, 46%)" });
    else if (p.sent) tags.push({ label: "sent", color: "hsl(150, 10%, 46%)" });
    else if (p.hard_bounced === false) tags.push({ label: "not opened", color: "hsl(150, 10%, 46%)" });
  }
  return tags;
}


// Self-contained: fetches the current internal user's followed-activity feed on
// mount and on a poll interval. Intentionally decoupled from the main dashboard
// payload so the (heavier) timeline aggregation never blocks the dashboard render.
const FollowedActivityCard = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    const fetchFeed = () => {
      sh.get("/internal/dashboard/followed_activity")
        .then((res) => {
          if (!active) return;
          setActivities(Array.isArray(res.data?.activities) ? res.data.activities : []);
        })
        .catch(() => {})
        .finally(() => {
          if (active) setLoaded(true);
        });
    };

    fetchFeed();
    const timer = setInterval(fetchFeed, POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  const visible = activities.slice(0, MAX_VISIBLE);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "0.75rem",
        border: "1px solid hsl(140 8% 88%)",
        overflow: "hidden",
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: "1px solid hsl(140 8% 88%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(150 30% 12%)",
          }}
        >
          Following
        </Typography>
      </Box>

      <Box>
        {visible.length === 0 ? (
          <Typography
            sx={{
              fontSize: 13,
              color: "hsl(150 10% 46%)",
              textAlign: "center",
              py: 4,
              px: 2.5,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
            }}
          >
            {loaded
              ? "Follow companies and candidates to see their activity here."
              : "Loading…"}
          </Typography>
        ) : (
          visible.map((act, idx) => {
            const source = act.source || {};
            const cfg = eventConfig(act.type);
            const clickable = Boolean(source.path);
            const detail = eventDetail(act);
            const statusTags = emailStatusTags(act);

            return (
              <Box
                key={act.id || `fa-${idx}`}
                onClick={clickable ? () => navigate(source.path) : undefined}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  px: 2.5,
                  py: "10px",
                  borderTop: idx === 0 ? "none" : "1px solid hsl(140 8% 88%)",
                  cursor: clickable ? "pointer" : "default",
                  transition: "background-color 120ms ease",
                  "&:hover": clickable ? { backgroundColor: "hsl(140 10% 97%)" } : undefined,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: cfg.color,
                    flexShrink: 0,
                    mt: "5px",
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Chip
                      label={cfg.label}
                      size="small"
                      sx={{
                        height: 18,
                        borderRadius: "6px",
                        fontSize: 10,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        color: cfg.color,
                        backgroundColor: cfg.bg,
                        flexShrink: 0,
                        "& .MuiChip-label": { px: "6px" },
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        color: "hsl(150 30% 12%)",
                        lineHeight: 1.5,
                        minWidth: 0,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {source.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        color: "hsl(150 10% 46%)",
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {relativeTime(act.occurred_at) || "just now"}
                    </Typography>
                  </Box>
                  {detail && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        color: "hsl(150 20% 30%)",
                        lineHeight: 1.45,
                        mt: "2px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {detail}
                    </Typography>
                  )}
                  {statusTags.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", mt: "3px" }}>
                      {statusTags.map((tag) => (
                        <Box
                          key={tag.label}
                          sx={{
                            fontSize: 9,
                            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                            color: tag.color,
                            backgroundColor: `${tag.color.replace(")", ", 0.12)").replace("hsl", "hsla")}`,
                            borderRadius: "4px",
                            px: "5px",
                            py: "1px",
                            lineHeight: 1.4,
                          }}
                        >
                          {tag.label}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
};

export default FollowedActivityCard;
